import { loadRouteLocation, type Router } from "vue-router";

const RETRY_KEY = "chunk-load-retries";
const MAX_RETRIES = 1;
let recovering = false;

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk [\d]+ failed|Couldn't resolve component/i;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return CHUNK_ERROR_PATTERN.test(message);
}

function reloadTo(path: string) {
  if (recovering) {
    return;
  }

  const retries = Number(sessionStorage.getItem(RETRY_KEY) ?? "0");

  if (retries >= MAX_RETRIES) {
    recovering = false;
    sessionStorage.removeItem(RETRY_KEY);
    return;
  }

  recovering = true;
  sessionStorage.setItem(RETRY_KEY, String(retries + 1));
  window.location.assign(path);
}

export function setupChunkLoadRecovery(router: Router): void {
  const viteChunkErrors = new Set<unknown>();

  router.beforeEach(async (to) => {
    viteChunkErrors.clear();

    try {
      await loadRouteLocation(to);

      recovering = false;
      sessionStorage.removeItem(RETRY_KEY);
    } catch (error) {
      if (!isChunkLoadError(error)) {
        throw error;
      }

      reloadTo(to.fullPath);
      return false;
    }
  });

  window.addEventListener("vite:preloadError", (event) => {
    const preloadEvent = event as VitePreloadErrorEvent;
    const error = preloadEvent.payload ?? event;
    viteChunkErrors.add(error);

    if (!isChunkLoadError(error)) {
      return;
    }

    preloadEvent.preventDefault();
  });

  router.onError((error, to) => {
    if (!isChunkLoadError(error) && !viteChunkErrors.has(error)) {
      return;
    }

    reloadTo(to.fullPath);
  });
}
